import { Check } from "@mui/icons-material";
import { Skeleton } from "@mui/material";
import Checkmark from "assets/img/svg/checkmark";
import Replay from "assets/img/svg/replay";
import type { ActionCardAction } from "components/shared/ActionCard";
import ActionCard from "components/shared/ActionCard";
import StyledButton from "components/shared/StyledButton";
import { DeclarationStatus } from "components/URSSAFDeclarationPage";
import { formatLocaleDateString, formatNumber } from "helpers/utils";
import { t } from "i18next";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IDeclaration } from "store/contributionStore";
import type { IPayment } from "store/paymentSlice";
import { fetchPaymentsBetweenDates } from "store/paymentSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { TFetchStatus } from "store/types";
import { v4 as uuidv4 } from "uuid";

import variables from "../../../assets/styles/_variables.module.scss";
import { getPaymentsInDeclaration, getReducedPayments } from "../DeclarationModal/utils";
import styles from "../style.module.scss";
import type { DeclarationsCardsData } from "../utils";
import { getDeclarationPeriod, savePaymentFetchType } from "../utils";

interface ICurrentDeclarations {
    /**
     * Array with all declarations.
     */
    cards: DeclarationsCardsData[];
    /**
     * Sets chosen declaration.
     */
    setCurrentDeclaration: Dispatch<SetStateAction<IDeclaration | DeclarationsCardsData | undefined>>;
}

/**
 * Block within DeclarationPage for all current declarations (open or late).
 */
const CurrentDeclarations = ({ cards, setCurrentDeclaration }: ICurrentDeclarations) => {
    const [t] = useTranslation();

    /**
     * Handles action from card.
     * @param declaration Data of declaration clicked on.
     * @param action Action clicked on.
     */
    const handleActionClick = async (declaration: DeclarationsCardsData, action?: ActionCardAction) => {
        await setCurrentDeclaration(declaration);
        if (action) {
            action.onClick();
        } else {
            (declaration.action as ActionCardAction)?.onClick();
        }
    };

    /**
     * Declarations that need to be done.
     */
    const currentDeclarations = useMemo(() => {
        return cards.filter((card) => card.timeStatus === "current");
    }, [cards]);

    /**
     * Last declaration made (needed for the "success" card).
     */
    const lastDeclaration = useMemo(() => {
        return cards.filter((card) => card.timeStatus === "history")[0];
    }, [cards]);

    return (
        <div className={styles["current-declarations-block"]}>
            {currentDeclarations.length > 0 ? (
                <div className={styles["cards-container"]}>
                    {currentDeclarations.map((card) => {
                        const backgroundColor = card?.style?.backgroundColor;
                        return card.declarationStatus === "neutral" && card.type === "urssaf" ? (
                            <OpenDeclarationComponent card={card} handleClick={handleActionClick} />
                        ) : (
                            <ActionCard
                                {...card}
                                key={`current-card-${uuidv4()}`}
                                usesGradient
                                style={{
                                    ...card.style,
                                    background: `linear-gradient(${backgroundColor}, ${backgroundColor}) padding-box, ${variables["gradient-bottom"]} border-box`,
                                }}
                                handleClick={(action) => handleActionClick(card, action)}
                            />
                        );
                    })}
                </div>
            ) : (
                <ActionCard
                    icon={<Checkmark />}
                    title={t("Declare.DeclareURSSAFAllDoneTitle")}
                    description={
                        lastDeclaration
                            ? t("Declare.DeclareURSSAFAllDoneDescription", {
                                  date: formatLocaleDateString({ date: new Date(lastDeclaration.declaredAt ?? "") }),
                              })
                            : ""
                    }
                    style={{ backgroundColor: "#ECFAED", padding: "10px 20px" }}
                    titleStyle={{ fontSize: "14px", fontWeight: "400" }}
                />
            )}
        </div>
    );
};
export default CurrentDeclarations;

/**
 * Action Card for the declaration currently open.
 * Adding extra data to regular ActionCard component would have been too messy just for one card. Especially if it's not meant to be used only for URSSAF.
 * @param card DeclarationsCardsData
 * @returns JSX Component
 */
const OpenDeclarationComponent = ({
    card,
    handleClick,
}: {
    /**
     * Data of open declaration.
     */
    card: DeclarationsCardsData;
    handleClick: (declaration: DeclarationsCardsData, action?: ActionCardAction) => Promise<void>;
}) => {
    const dispatch = useAppDispatch();
    const backgroundColor = card?.style?.backgroundColor;

    const [periodPayments, setPeriodPayments] = useState<IPayment[] | undefined>();

    const currentPayments = useMemo(() => {
        if (!periodPayments) return [];
        return getPaymentsInDeclaration({ payments: periodPayments, card });
    }, [periodPayments]);

    const getTotalAmountContributions = useMemo(() => {
        return currentPayments.reduce((a, { contributions }) => contributions + a, 0) / 100;
    }, [currentPayments]);

    const getTotalPaidBeneficiaries = useMemo(() => {
        return getReducedPayments(currentPayments).length ?? 0;
    }, [currentPayments]);

    // Fetches payments linked to period for open declarations.
    // --> TODO: check if there is better way to handle this once partial payment implemented.
    useEffect(() => {
        const dateBoundaries = getDeclarationPeriod(card.year, card.quarter);

        dispatch(
            fetchPaymentsBetweenDates({
                startDate: new Date(dateBoundaries.startDate).toLocaleDateString("en-CA"),
                endDate: new Date(dateBoundaries.endDate).toLocaleDateString("en-CA"),
                forURSSAF: "true",
            }),
        ).then((resp) => {
            if (resp.meta.requestStatus === "fulfilled") {
                setPeriodPayments(resp.payload.result);
            }
        });
        savePaymentFetchType("declarations");
    }, []);

    return (
        <div
            className="action-card gradient"
            style={{
                background: `linear-gradient(${backgroundColor}, ${backgroundColor}) padding-box, ${variables["gradient-bottom"]} border-box`,
                padding: "0px",
                display: "flex",
                flexDirection: "column",
            }}
            key={`open-declaration-${uuidv4()}`}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                    justifyContent: "space-between",
                    width: "100%",
                    borderBottom: "1px solid #eaeaea",
                    padding: "32px 48px",
                }}
            >
                <div className="action-card-main">
                    <div className="action-card-head">
                        {card.icon && <div className="action-card-icon">{card.icon}</div>}
                        <div className="action-card-title" style={{ display: "flex", alignItems: "center" }}>
                            {card.title}
                        </div>
                    </div>
                    {card.action && <div className="action-card-description">{card.description}</div>}
                </div>
                <div className={"action-card-action"}>
                    {card.downloadedAt ? (
                        <>
                            <StyledButton
                                buttonType="outlined"
                                sx={{ backgroundColor: "#FAFAFA !important", paddingLeft: "4px !important" }}
                                onClick={() => handleClick(card, card?.action && card?.action[0])}
                            >
                                <Check />
                                {t("Declarations.SetAsDeclared") as string}
                            </StyledButton>
                            <StyledButton
                                onClick={() => handleClick(card, card?.action && card?.action[1])}
                                className={"redownload-btn"}
                                buttonType="outlined"
                                sx={{ backgroundColor: "#FAFAFA !important" }}
                            >
                                <Replay />
                            </StyledButton>
                        </>
                    ) : (
                        card.action &&
                        !Array.isArray(card.action) && (
                            <>
                                <StyledButton buttonType="primary" onClick={() => handleClick(card)}>
                                    {card.action.icon}
                                    {card.action.label}
                                </StyledButton>
                            </>
                        )
                    )}
                </div>
            </div>
            <div className={styles["action-card-data"]}>
                <div>
                    <span>{t("Declarations.NetTotalPeriod") as string}</span>
                    {!periodPayments ? (
                        <Skeleton variant="text" />
                    ) : (
                        <span>{formatNumber(getTotalAmountContributions, { isCurrency: true })}</span>
                    )}
                </div>
                <div>
                    <span>{t("Declarations.BeneficiariesNumberPeriod") as string}</span>
                    {!periodPayments ? <Skeleton variant="text" /> : <span>{getTotalPaidBeneficiaries}</span>}
                </div>
            </div>
        </div>
    );
};
